import React from 'react';
import DeltaBikePromoBanner from './DeltaBikePromoBanner';
import OrganizerPromoBanner from './OrganizerPromoBanner';
import PartnerPromoBanner from './PartnerPromoBanner';
import WinterchallengePromoBanner from './WinterchallengePromoBanner';

/** Pfeile und Punkte, die der Werbeplatz in die gerade sichtbare Anzeige legt. */
export type PromoControls = (variant: 'desktop' | 'mobile') => React.ReactNode;

export interface AdBannerDef {
  /** Schlüssel in `ad_banners` (V180) und zugleich Slot-Id der Banner-Messung. */
  key: string;
  /** Name in `/admin/werbeplatz`, den Geschäftszahlen und am Punkt im Werbeplatz. */
  label: string;
  /** Ein Satz für die Verwaltung: was die Anzeige zeigt und wohin sie führt. */
  description: string;
  render: (controls?: PromoControls) => React.ReactElement;
}

/**
 * Alle Anzeigen, die der Werbeplatz über der Eventliste kennt, in der Reihenfolge, in der sie
 * sich abwechseln. Welche davon laufen, schaltet die Redaktion unter `/admin/werbeplatz`.
 *
 * Eine neue Anzeige braucht zwei Dinge: einen Eintrag hier und eine Zeile in `ad_banners`
 * (Migration). Ohne Zeile lässt sie sich nicht einschalten; ein Schlüssel ohne Eintrag hier wird
 * im Werbeplatz übergangen.
 */
export const AD_BANNERS: AdBannerDef[] = [
  {
    key: 'delta-bike',
    label: 'Delta Bike (Equipment-Partner)',
    description: 'Partner-Anzeige mit Logo. Die ganze Fläche führt zum Shop von Delta Bike.',
    render: controls => <DeltaBikePromoBanner controls={controls} />,
  },
  {
    key: 'organizer-promo',
    label: 'Für Veranstalter („Mehr Leute am Start“)',
    description: 'Eigene Anzeige. Führt auf die Seite „Für Veranstalter“.',
    render: controls => <OrganizerPromoBanner controls={controls} />,
  },
  {
    key: 'partner-promo',
    label: 'Hier werben („Deine Marke. Mitten im Radsport“)',
    description: 'Eigene Anzeige. Führt auf die Seite „Für Partner“, Anfragen stehen unter dem Thema „Werbeplatz“.',
    render: controls => <PartnerPromoBanner controls={controls} />,
  },
  {
    key: 'winterchallenge',
    label: 'Winterchallenge (Kilometer sammeln)',
    description: 'Eigene Anzeige mit dem Stand der Community. Führt auf die Seite der Winterchallenge.',
    render: controls => <WinterchallengePromoBanner controls={controls} />,
  },
];

export const AD_BANNER_LABELS: Record<string, string> = AD_BANNERS.reduce(
  (acc, b) => ({ ...acc, [b.key]: b.label }), {} as Record<string, string>,
);

/**
 * Anzeigen, die es nicht mehr gibt, deren Zahlen aber noch im Zeitraum stehen können
 * (`tracking_events` hält sie). Nur für die Beschriftung in Verwaltung und Geschäftszahlen.
 */
export const FORMER_AD_BANNER_LABELS: Record<string, string> = {
  'tdf-promo': 'Tour de France (Rennkalender)',
  'social-promo': 'Social Media (Instagram und Facebook)',
};

/** Name einer Anzeige für Zahlen und Listen, auch einer früheren; sonst der Schlüssel. */
export const adBannerLabel = (key: string): string =>
  AD_BANNER_LABELS[key] ?? FORMER_AD_BANNER_LABELS[key] ?? key;
